import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { SHOP_CATEGORIES, ORDER_STATUS } from "@/lib/constants";
import ImageUploader from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Store, Package, ClipboardList, LogOut, Plus, Pencil, Trash2,
  Loader2, DollarSign, ShoppingBag, Clock, Phone, MapPin, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, X
} from "lucide-react";
import { toast } from "sonner";
import { playNotificationSound } from "@/lib/sound";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";
import MobileTabBar from "@/components/MobileTabBar";

const SELLER_STEPS = { pending: "accepted", accepted: "preparing", preparing: "ready" };
const NEXT_LABEL = { pending: "قبول الطلب", accepted: "بدء التحضير", preparing: "جاهز للتوصيل" };
const normalizeOrders = (orders) => Array.isArray(orders) ? orders.map((order) => order?.data || order) : [];

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const seenPending = useRef(null);
  usePushNotifications();

  const notifyNew = (list) => {
    const pend = list.filter((o) => o.status === "pending");
    if (seenPending.current !== null) {
      const fresh = pend.filter((o) => !seenPending.current.has(o.id));
      if (fresh.length > 0) {
        toast.info(`لديك ${fresh.length} طلب جديد!`, { duration: 6000 });
        playNotificationSound();
      }
    }
    seenPending.current = new Set(pend.map((o) => o.id));
  };

  const loadAll = async (isBackground = false) => {
    try {
      const s = await api.get("/shops/mine");
      setShop(s.data);
      if (s.data) {
        const [p, o, st] = await Promise.all([
          api.get("/products/mine"),
          api.get("/orders/shop"),
          api.get("/seller/stats")
        ]);
        setProducts(p.data);
        const normOrders = normalizeOrders(o.data);
        notifyNew(normOrders);
        setOrders(normOrders);
        setStats(st.data);
      }
    } catch (e) {
      if (!isBackground) toast.error("تعذر تحميل البيانات");
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    loadAll(false);
    const t = setInterval(() => loadAll(true), 6000);
    return () => clearInterval(t);
  }, []);

  const advance = async (o) => {
    const next = SELLER_STEPS[o.status];
    if (!next) return;
    try {
      await api.put(`/orders/${o.id}/status`, { status: next });
      toast.success("تم تحديث حالة الطلب");
      loadAll(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const rejectOrder = async (o) => {
    if (!window.confirm("هل أنت متأكد من رفض/إلغاء هذا الطلب؟")) return;
    try {
      await api.put(`/orders/${o.id}/status`, { status: "cancelled" });
      toast.success("تم إلغاء الطلب");
      loadAll(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!shop) return <ShopSetup onDone={() => loadAll(false)} onLogout={() => { logout(); navigate("/"); }} />;

  const navItems = [
    { id: "orders", label: "الطلبات الواردة", icon: ClipboardList, badge: stats.pending },
    { id: "products", label: "المنتجات", icon: Package },
    { id: "shop", label: "إعدادات المتجر", icon: Store }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0" dir="rtl">
      {/* Desktop Sidebar */}
      <aside className="brand-panel hidden md:flex w-64 shrink-0 text-white flex-col p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-10">
          <BrandLogo className="h-11 w-[124px] object-contain" />
          <div>
            <span className="font-display font-bold block text-base leading-tight">{shop.name}</span>
            <span className="text-[11px] text-white/70">لوحة تحكم البائع</span>
          </div>
        </div>
        <nav className="space-y-1.5 flex-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                tab === item.id ? "bg-white text-primary shadow-md font-bold" : "text-primary-foreground/80 hover:bg-white/10 hover:text-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </div>
              {item.badge > 0 && <span className={`px-2 py-0.5 rounded-full text-xs font-mono font-bold ${tab === item.id ? "bg-primary text-white" : "bg-amber-500 text-white"}`}>{item.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="pt-4 border-t border-white/15 flex items-center justify-between">
          <ColorThemeSwitcher variant="desktop" />
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile Header */}
        <header className="safe-top-header md:hidden glass sticky top-0 z-30 border-b border-border flex items-center justify-between gap-3 px-4 h-14 bg-card/95 backdrop-blur-md">
          <div className="flex min-w-0 items-center gap-2">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Store className="h-4 w-4" /></div>
            <div className="min-w-0">
              <span className="block truncate text-sm font-extrabold">{shop.name}</span>
              <span className="block text-[10px] text-muted-foreground">لوحة البائع · {stats.pending} طلب جديد</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ColorThemeSwitcher variant="mobile" />
            <button onClick={() => { logout(); navigate("/"); }} aria-label="تسجيل الخروج" className="grid h-10 w-10 place-items-center rounded-full text-muted-foreground active:bg-muted">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="p-3 pb-5 md:p-8 max-w-6xl w-full mx-auto flex-1">
          {tab === "orders" && <OrdersTab orders={orders} advance={advance} reject={rejectOrder} />}
          {tab === "products" && <ProductsTab products={products} reload={() => loadAll(true)} />}
          {tab === "shop" && <ShopTab shop={shop} reload={() => loadAll(true)} />}
        </main>

        <MobileTabBar
          items={navItems.map((item) => ({ ...item, label: item.id === "orders" ? "الطلبات" : item.id === "products" ? "المنتجات" : "المتجر" }))}
          activeId={tab}
          onSelect={(id) => setTab(id)}
          label="قائمة البائع"
        />
      </div>
    </div>
  );
}

function OrdersTab({ orders, advance, reject }) {
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const filtered = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "active") return !["delivered", "cancelled"].includes(o.status);
    return o.status === filter;
  });

  const handleAdvance = async (o) => {
    if (processingId === o.id) return;
    setProcessingId(o.id);
    try { await advance(o); } finally { setProcessingId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl tracking-tight">الطلبات الواردة</h2>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none">
        {["all", "active", "pending", "accepted", "preparing", "ready", "delivered"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl text-xs font-bold shrink-0 ${filter === f ? "bg-primary text-white" : "bg-card border border-border"}`}>
            {f === "all" ? "الكل" : f === "active" ? "النشطة" : ORDER_STATUS[f]?.label || f}
          </button>
        ))}
      </div>
      <div className="space-y-3.5">
        {filtered.map((o) => {
          const st = ORDER_STATUS[o.status] || { label: o.status, color: "bg-muted text-muted-foreground" };
          const isPending = o.status === "pending";
          const isExpanded = expandedId === o.id || isPending;
          return (
            <div key={o.id} className={`bg-card rounded-2xl border p-4 ${isPending ? "border-amber-500 shadow-md" : "border-border"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center font-bold text-sm ${isPending ? "bg-amber-500 text-white" : "bg-primary/10 text-primary"}`}>#{o.id.slice(0, 3)}</div>
                  <div>
                    <p className="font-bold text-sm">{o.buyer_name}</p>
                    <p className="text-xs text-muted-foreground" dir="ltr"><a href={`tel:${o.phone}`} className="hover:underline">{o.phone}</a></p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${st.color}`}>{st.label}</span>
              </div>
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <div className="text-xs text-muted-foreground">عنوان التوصيل: <span className="font-semibold text-foreground">{o.delivery_address}</span></div>
                  {o.note && <div className="text-xs bg-amber-500/10 text-amber-800 p-2 rounded-lg">ملاحظة الزبون: {o.note}</div>}
                  {o.items.map((it) => <div key={it.product_id} className="flex justify-between text-sm"><span className="text-muted-foreground">{it.name} × {it.qty}</span><span className="font-mono">{fmtPrice(it.price * it.qty)}</span></div>)}
                  <div className="flex justify-between font-bold pt-2 border-t"><span>الإجمالي</span><span>{fmtPrice(o.total)}</span></div>
                  <div className="flex gap-2 pt-2">
                    {NEXT_LABEL[o.status] && (
                      <Button onClick={() => handleAdvance(o)} disabled={processingId === o.id} className="flex-1 rounded-xl h-11 font-bold">
                        {processingId === o.id ? <Loader2 className="animate-spin" /> : NEXT_LABEL[o.status]}
                      </Button>
                    )}
                    {isPending && (
                      <Button variant="outline" onClick={() => reject(o)} className="rounded-xl h-11 text-destructive border-destructive/30 hover:bg-destructive/10">
                        <X className="w-4 h-4 ml-1" /> رفض الطلب
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductForm({ initial, onSaved }) {
  const [f, setF] = useState(initial || { name: "", description: "", price: "", image_path: "", category: "عام", in_stock: true, discount_percent: 0 });
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name || !f.price) { toast.error("أدخل الاسم والسعر"); return; }
    setSaving(true);
    try {
      const payload = { ...f, price: Number(f.price), discount_percent: Number(f.discount_percent) || 0 };
      if (initial?.id) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success("تم الحفظ");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <ImageUploader value={f.image_path} onUploadingChange={setImageUploading} onChange={(p) => setF((current) => ({ ...current, image_path: p }))} testid="product-image" aspect="portrait" hint="مقاس مثالي 1080×1350" />
      <div className="space-y-1.5"><Label>اسم المنتج</Label><Input value={f.name} onChange={set("name")} /></div>
      <div className="space-y-1.5"><Label>الوصف</Label><Textarea value={f.description} onChange={set("description")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>السعر (ل.س)</Label><Input type="number" value={f.price} onChange={set("price")} /></div>
        <div className="space-y-1.5"><Label>خصم %</Label><Input type="number" value={f.discount_percent} onChange={set("discount_percent")} /></div>
      </div>
      <div className="space-y-1.5">
        <Label>التصنيف</Label>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SHOP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between p-3 rounded-xl border bg-muted/20">
        <Label>متوفر في المخزون</Label>
        <Switch checked={f.in_stock} onCheckedChange={(v) => setF({ ...f, in_stock: v })} />
      </div>
      <Button onClick={save} disabled={saving || imageUploading} className="w-full rounded-xl h-11 font-bold">{saving ? <Loader2 className="animate-spin" /> : "حفظ المنتج"}</Button>
    </div>
  );
}

function ProductsTab({ products, reload }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المنتج؟")) return;
    try { await api.delete(`/products/${id}`); toast.success("تم الحذف"); reload(); } catch { toast.error("تعذّر الحذف"); }
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl">المنتجات</h2>
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="rounded-xl"><Plus className="w-4 h-4 ms-1" /> منتج جديد</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-card rounded-2xl border p-4 flex flex-col justify-between shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0">
                {p.image_path ? <img src={fileUrl(p.image_path)} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 m-auto text-muted-foreground/40" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm truncate">{p.name}</h3>
                <p className="text-primary font-mono font-bold text-sm">{fmtPrice(p.price)}</p>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.in_stock ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"}`}>
                  {p.in_stock ? "متوفر" : "نفد"}
                </span>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t">
              <Button variant="outline" size="sm" onClick={() => { setEdit(p); setOpen(true); }} className="flex-1 h-8"><Pencil className="w-3.5 h-3.5 ml-1" /> تعديل</Button>
              <Button variant="outline" size="sm" onClick={() => del(p.id)} className="h-8 text-destructive hover:bg-destructive/10"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto sm:max-w-md"><DialogHeader><DialogTitle>{edit ? "تعديل المنتج" : "منتج جديد"}</DialogTitle></DialogHeader><ProductForm initial={edit} onSaved={() => { setOpen(false); reload(); }} /></DialogContent></Dialog>
    </div>
  );
}

function ShopTab({ shop, reload }) {
  const [f, setF] = useState(shop);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/shops/${shop.id}`, { ...f, delivery_fee: Number(f.delivery_fee) });
      toast.success("تم تحديث إعدادات المتجر بنجاح");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="seller-shop-tab">
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">إعدادات المتجر</h2>
        <p className="text-xs text-muted-foreground mt-0.5">تعديل شعار المتجر، أرقام التواصل، رسوم التوصيل، وحالة الاستقبال</p>
      </div>

      <div className="space-y-4 bg-card rounded-2xl p-6 border border-border shadow-sm">
        <ImageUploader value={f.image_path} onUploadingChange={setImageUploading} onChange={(p) => setF((current) => ({ ...current, image_path: p }))} testid="shop-image" />
        <div className="space-y-1.5"><Label>اسم المتجر *</Label><Input value={f.name} onChange={set("name")} /></div>
        <div className="space-y-1.5"><Label>وصف المتجر</Label><Textarea value={f.description} onChange={set("description")} /></div>
        <div className="space-y-1.5">
          <Label>التصنيف الرئيسي</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{SHOP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>رقم الهاتف</Label><Input value={f.phone} onChange={set("phone")} dir="ltr" className="text-start" /></div>
          <div className="space-y-1.5"><Label>رسوم التوصيل (ل.س)</Label><Input type="number" value={f.delivery_fee} onChange={set("delivery_fee")} dir="ltr" className="text-start" /></div>
        </div>
        <div className="space-y-1.5"><Label>ساعات العمل (مثال: 9 صباحاً - 11 مساءً)</Label><Input value={f.hours} onChange={set("hours")} /></div>
        <div className="space-y-1.5"><Label>العنوان التفصيلي</Label><Input value={f.address} onChange={set("address")} /></div>
        <div className="flex items-center justify-between p-3 rounded-xl border bg-muted/20">
          <Label className="cursor-pointer font-semibold">استقبال الطلبات (المتجر مفتوح)</Label>
          <Switch checked={f.is_open} onCheckedChange={(v) => setF({ ...f, is_open: v })} />
        </div>
        <Button onClick={save} disabled={saving || imageUploading} className="w-full rounded-xl h-11 font-bold">
          {saving && <Loader2 className="w-4 h-4 ms-2 animate-spin" />}
          حفظ تغييرات المتجر
        </Button>
      </div>
    </div>
  );
}

function ShopSetup({ onDone, onLogout }) {
  const [f, setF] = useState({ name: "", description: "", category: SHOP_CATEGORIES[0], image_path: "", phone: "", address: "", delivery_fee: 3000, is_open: true });
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    if (!f.name) { toast.error("أدخل اسم المتجر"); return; }
    setSaving(true);
    try {
      await api.post("/shops", { ...f, delivery_fee: Number(f.delivery_fee) });
      toast.success("تم إنشاء متجرك بنجاح!");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="min-h-screen bg-background grid place-items-center p-6" dir="rtl" data-testid="shop-setup">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary grid place-items-center text-primary-foreground font-display font-bold shadow-md"><Store className="w-6 h-6" /></div>
            <div><h1 className="font-display font-bold text-xl">أنشئ متجرك</h1><p className="text-xs text-muted-foreground">ابدأ ببيع منتجاتك واستقبال طلبات الزبائن في كفرنبل</p></div>
          </div>
          <button onClick={onLogout} className="text-muted-foreground hover:text-destructive p-2"><LogOut className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4 bg-card rounded-3xl p-6 md:p-8 border border-border shadow-lg">
          <ImageUploader value={f.image_path} onUploadingChange={setImageUploading} onChange={(p) => setF((current) => ({ ...current, image_path: p }))} testid="setup-image" />
          <div className="space-y-1.5"><Label>اسم المتجر *</Label><Input value={f.name} onChange={set("name")} placeholder="مثال: مطعم الأصيل" /></div>
          <div className="space-y-1.5"><Label>وصف المتجر</Label><Textarea value={f.description} onChange={set("description")} placeholder="أشهى الوجبات والمأكولات..." /></div>
          <div className="space-y-1.5">
            <Label>التصنيف</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SHOP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>الهاتف</Label><Input value={f.phone} onChange={set("phone")} dir="ltr" className="text-start" placeholder="+9639..." /></div>
            <div className="space-y-1.5"><Label>رسوم التوصيل (ل.س)</Label><Input type="number" value={f.delivery_fee} onChange={set("delivery_fee")} dir="ltr" className="text-start" /></div>
          </div>
          <div className="space-y-1.5"><Label>ساعات العمل</Label><Input value={f.hours} onChange={set("hours")} placeholder="مثال: 9 صباحاً - 11 مساءً" /></div>
          <div className="space-y-1.5"><Label>العنوان التفصيلي</Label><Input value={f.address} onChange={set("address")} placeholder="كفرنبل - الشارع العام" /></div>
          <Button onClick={create} disabled={saving || imageUploading} className="w-full rounded-xl h-12 font-bold text-base shadow-sm">
            {saving && <Loader2 className="w-5 h-5 ms-2 animate-spin" />}
            إنشاء المتجر وبدء العمل
          </Button>
        </div>
      </div>
    </div>
  );
}
